import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Loader2, History, Star, MessageCircle, Download, FileText, Lock } from 'lucide-react';
import { whatsappAPI } from './whatsappAPI';
import { formatWa } from './waFormat';

/* Histórico de atendimentos do WhatsApp: a conversa de cada atendimento finalizado (protocolo), para ler depois —
 * na ficha do contato (quem voltou a chamar) e na página do cliente. Só leitura. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const mediaUrl = (id, baixar = false) =>
  `${API}/whatsapp/media/${encodeURIComponent(id)}?token=${encodeURIComponent(localStorage.getItem('agrolink_token') || '')}${baixar ? '&baixar=1' : ''}`;
const fmtProt = (p) => (p ? (String(p).startsWith('#') ? p : `#${p}`) : '');
const dataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '');
const dia = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

function Estrelas({ n }) {
  return (
    <span className="inline-flex gap-0.5 align-middle" aria-label={`Avaliação ${n} de 5`}>
      {[1, 2, 3, 4, 5].map(i => <Star key={i} className={`w-3 h-3 ${i <= n ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}
    </span>
  );
}

function Anexo({ m }) {
  const nome = m.media_filename || 'arquivo';
  if (m.message_type === 'image' || (m.message_type === 'sticker' && (m.media_mime || '').startsWith('image/'))) {
    return <a href={mediaUrl(m.id)} target="_blank" rel="noopener noreferrer"><img src={mediaUrl(m.id)} alt="imagem" loading="lazy" className="rounded max-h-48 mb-1" /></a>;
  }
  return (
    <span className="flex items-center gap-2 mb-1">
      <FileText className="w-4 h-4 text-slate-500 shrink-0" />
      <a href={mediaUrl(m.id)} target="_blank" rel="noopener noreferrer" className="underline truncate max-w-[220px]">{m.message_type === 'audio' ? 'Áudio' : m.message_type === 'video' ? 'Vídeo' : nome}</a>
      <a href={mediaUrl(m.id, true)} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" title="Baixar" aria-label={`Baixar ${nome}`}><Download className="w-3.5 h-3.5" /></a>
    </span>
  );
}

export function HistoricoModal({ atId, onClose }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  useEffect(() => {
    whatsappAPI.historicoAtendimento(atId).then(r => setDados(r.data))
      .catch(e => setErro(e?.response?.data?.detail || 'Não foi possível carregar o histórico'));
  }, [atId]);
  const at = dados?.atendimento;
  let diaAnterior = null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg max-h-[88vh] shadow-xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}
        role="dialog" aria-label="Histórico do atendimento" data-testid="wa-historico-modal">
        <div className="px-5 py-3 border-b dark:border-slate-800 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h3 className="font-semibold flex items-center gap-2"><History className="w-4 h-4" /> Atendimento {fmtProt(at?.protocolo)}</h3>
            {at && (
              <p className="text-xs text-slate-500 mt-0.5">
                {dados.contato?.cliente_nome || dados.contato?.push_name || ''}{(dados.contato?.cliente_nome || dados.contato?.push_name) ? ' · ' : ''}
                {at.atendente_nome || 'sem atendente'}{at.setor_nome ? ` · ${at.setor_nome}` : ''} · finalizado em {dataHora(at.fim)}
                {at.encerrado_por && at.encerrado_por !== at.atendente_nome ? ` por ${at.encerrado_por}` : ''}
                {at.avaliacao ? <> · <Estrelas n={at.avaliacao} /></> : null}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 shrink-0" aria-label="Fechar"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 bg-[#efeae2] dark:bg-slate-950 space-y-1.5" data-testid="wa-historico-mensagens">
          {!dados && !erro && <p className="text-center text-slate-500 py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></p>}
          {erro && <p className="text-center text-sm text-red-600 py-8">{erro}</p>}
          {at?.resumo_ia && (
            <div className="rounded-lg bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-900 p-2.5 text-xs text-violet-900 dark:text-violet-200 mb-2 whitespace-pre-wrap">
              <b>Resumo da IA:</b> {at.resumo_ia}
            </div>
          )}
          {dados && !dados.mensagens.length && <p className="text-center text-sm text-slate-500 py-8">Nenhuma mensagem registrada neste atendimento.</p>}
          {dados?.mensagens.map(m => {
            const d = dia(m.timestamp);
            const sep = d !== diaAnterior ? (diaAnterior = d) : null;
            const interno = m.message_type === 'nota' || m.message_type === 'sistema' || m.interno;
            return (
              <React.Fragment key={m.id}>
                {sep && <div className="text-center"><span className="text-[11px] px-2 py-0.5 rounded-full bg-white/80 dark:bg-slate-800 text-slate-500">{sep}</span></div>}
                {interno ? (
                  <div className="flex justify-center">
                    <span className={`text-[11.5px] px-2.5 py-1 rounded-lg max-w-[90%] ${m.message_type === 'nota' ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100' : 'bg-white/70 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                      {m.message_type === 'nota' && <Lock className="w-3 h-3 inline -mt-0.5 mr-1" />}
                      {m.message_type === 'nota' && m.sent_by_name ? <b>{m.sent_by_name}: </b> : null}{m.text}
                    </span>
                  </div>
                ) : (
                  <div className={`flex ${m.from_me ? 'justify-end' : ''}`}>
                    <div className={`rounded-lg px-2.5 py-1.5 text-[13px] max-w-[85%] shadow-sm text-slate-900 ${m.from_me ? 'bg-[#d9fdd3]' : 'bg-white'}`}>
                      {m.from_me && m.sent_by_name && <p className="text-[11px] font-semibold text-emerald-800">{m.sent_by_name}</p>}
                      {m.media_file && <Anexo m={m} />}
                      {m.text && !(m.media_file && /^\[(imagem|áudio|vídeo|sticker|documento)/.test(m.text)) && (
                        <p className="whitespace-pre-wrap break-words">{formatWa(m.text)}</p>
                      )}
                      <p className="text-[10px] text-slate-500 text-right">{hora(m.timestamp)}</p>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* Aba "WhatsApp" da página do cliente. */
export function AtendimentosCliente({ clienteId }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [aberto, setAberto] = useState(null);
  useEffect(() => {
    whatsappAPI.atendimentosCliente(clienteId).then(r => setDados(r.data))
      .catch(e => setErro(e?.response?.status === 403 ? 'Você não tem acesso ao atendimento WhatsApp.' : 'Não foi possível carregar os atendimentos.'));
  }, [clienteId]);
  if (erro) return <p className="text-sm text-muted-foreground py-6 text-center">{erro}</p>;
  if (!dados) return <p className="text-center py-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" /></p>;
  return (
    <div className="space-y-3" data-testid="cliente-wa-atendimentos">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {dados.atendimentos.length ? `${dados.atendimentos.length} atendimento(s) pelo WhatsApp` : 'Nenhum atendimento finalizado pelo WhatsApp ainda.'}
        </p>
        {dados.conversas?.[0] && (
          <Link to={`/whatsapp?jid=${encodeURIComponent(dados.conversas[0])}`} className="h-8 px-3 rounded-lg border text-sm font-medium flex items-center gap-1.5 hover:bg-muted">
            <MessageCircle className="w-4 h-4" /> Abrir conversa
          </Link>
        )}
      </div>
      <div className="divide-y rounded-xl border">
        {dados.atendimentos.map(a => (
          <button key={a.id} onClick={() => setAberto(a.id)} data-testid={`cliente-wa-at-${a.id}`}
            className="w-full text-left px-4 py-3 hover:bg-muted/50 flex items-center gap-3">
            <History className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="text-sm font-medium">{dataHora(a.fim)}</span>
              <span className="text-xs text-muted-foreground ml-2">{fmtProt(a.protocolo)}</span>
              <span className="block text-xs text-muted-foreground truncate">
                {a.atendente_nome || 'sem atendente'}{a.setor_nome ? ` · ${a.setor_nome}` : ''}{a.modo === 'arquivado' ? ' · arquivado' : ''}
                {a.resumo_ia ? ` · ${a.resumo_ia}` : ''}
              </span>
            </span>
            {a.avaliacao ? <Estrelas n={a.avaliacao} /> : null}
            <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400 shrink-0">Ver conversa</span>
          </button>
        ))}
      </div>
      {aberto && <HistoricoModal atId={aberto} onClose={() => setAberto(null)} />}
    </div>
  );
}
